import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Trophy, Users2, Globe2, Home as HomeIcon } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

/* Calm-style leaderboard — shows display names + points only. No ages, no
   emails. Three strategic tabs:
   - Family  (you + your children; requires sign-in)
   - Language (top players in a single live language; public)
   - Global  (top overall; public)
*/

const LANGUAGES = [
    { slug: 'shona', name: 'Shona' },
    { slug: 'ndebele', name: 'Ndebele' },
    { slug: 'yoruba', name: 'Yoruba' },
    { slug: 'swahili', name: 'Swahili' },
    { slug: 'french', name: 'French' },
    { slug: 'portuguese', name: 'Portuguese' },
];

function Rank({ i }) {
    const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
    return (
        <div className="w-10 text-center font-display font-semibold text-lq-ink-500">
            {medal || `#${i + 1}`}
        </div>
    );
}

function Row({ row, i }) {
    return (
        <li className={`flex items-center gap-3 px-4 py-3 rounded-xl ${row.is_you ? 'bg-lq-peach-100 border border-lq-peach-300' : 'bg-white border border-lq-ink-100'}`}
            data-testid={`leaderboard-row-${i}`}>
            <Rank i={i} />
            <div className="w-10 h-10 rounded-full bg-lq-purple-100 inline-flex items-center justify-center text-lq-purple-700 font-display font-bold">
                {(row.name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-lq-ink-900 truncate">{row.name}{row.is_you && <span className="ml-2 text-xs text-lq-purple-700">(you)</span>}</div>
                <div className="text-xs text-lq-ink-500">{row.kind === 'child' ? 'Child profile' : 'Parent / adult'}</div>
            </div>
            <div className="text-right">
                <div className="font-display text-xl font-semibold text-lq-ink-900">{(row.points || 0).toLocaleString()}</div>
                <div className="text-xs text-lq-ink-500">points</div>
            </div>
        </li>
    );
}

export default function Leaderboard() {
    useSEO({
        title: 'Leaderboard — Linguistic Quest',
        description: 'See the top language-learning families on Linguistic Quest. Public leaderboards by language and overall.',
        path: '/leaderboard',
    });
    const { user } = useAuth();
    const isPaid = user && ['trialing', 'active'].includes((user.subscription_status || 'none').toLowerCase()) &&
        ['explorer', 'polyglot', 'global'].includes((user.subscription_tier || '').toLowerCase());
    const [tab, setTab] = useState(isPaid ? 'family' : 'global');
    const [lang, setLang] = useState('shona');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancel = false;
        setLoading(true); setErr(null);
        const params = { scope: tab, limit: 25 };
        if (tab === 'language') params.language_slug = lang;
        api.get('/leaderboard', { params })
            .then(({ data }) => { if (!cancel) setRows(data.rows || []); })
            .catch((e) => { if (!cancel) setErr(e?.response?.data?.detail || 'Could not load leaderboard'); })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [tab, lang]);

    return (
        <main className="max-w-3xl mx-auto px-6 py-12" data-testid="page-leaderboard">
            <div className="flex items-center gap-3">
                <Trophy size={28} className="text-lq-peach-600"/>
                <h1 className="font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">Leaderboard</h1>
            </div>
            <p className="mt-2 text-lq-ink-700 max-w-2xl">Points are a gentle way to celebrate progress — not a race. Only display names are shown, so real identities stay private.</p>

            <div className="mt-6 inline-flex rounded-full bg-lq-ink-100 p-1 text-sm font-semibold" role="tablist">
                {isPaid && (
                    <button onClick={() => setTab('family')} role="tab" aria-selected={tab === 'family'}
                        className={`px-4 py-2 rounded-full flex items-center gap-1.5 ${tab === 'family' ? 'bg-white text-lq-ink-900 shadow-sm' : 'text-lq-ink-700'}`}
                        data-testid="leaderboard-tab-family"><HomeIcon size={14}/> Family</button>
                )}
                <button onClick={() => setTab('language')} role="tab" aria-selected={tab === 'language'}
                    className={`px-4 py-2 rounded-full flex items-center gap-1.5 ${tab === 'language' ? 'bg-white text-lq-ink-900 shadow-sm' : 'text-lq-ink-700'}`}
                    data-testid="leaderboard-tab-language"><Users2 size={14}/> By language</button>
                <button onClick={() => setTab('global')} role="tab" aria-selected={tab === 'global'}
                    className={`px-4 py-2 rounded-full flex items-center gap-1.5 ${tab === 'global' ? 'bg-white text-lq-ink-900 shadow-sm' : 'text-lq-ink-700'}`}
                    data-testid="leaderboard-tab-global"><Globe2 size={14}/> Global</button>
            </div>

            {tab === 'language' && (
                <div className="mt-4 flex flex-wrap gap-2">
                    {LANGUAGES.map((L) => (
                        <button key={L.slug} onClick={() => setLang(L.slug)}
                            className={`px-3 py-1.5 rounded-full text-sm border-2 ${lang === L.slug ? 'bg-lq-purple-500 text-white border-lq-purple-500' : 'bg-white border-lq-ink-100 text-lq-ink-900 hover:border-lq-purple-300'}`}
                            data-testid={`leaderboard-lang-${L.slug}`}>{L.name}</button>
                    ))}
                </div>
            )}

            <section className="mt-6">
                {loading && <div className="text-lq-ink-500">Loading…</div>}
                {err && <div className="text-lq-error">{err}</div>}
                {!loading && !err && rows.length === 0 && (
                    <div className="bg-white border border-lq-ink-100 rounded-2xl p-8 text-center" data-testid="leaderboard-empty">
                        <p className="text-lq-ink-700">No points here yet — be the first! Finish any puzzle to earn your first points.</p>
                    </div>
                )}
                <ul className="space-y-2">
                    {rows.map((r, i) => <Row key={i} row={r} i={i}/>) }
                </ul>
            </section>

            <div className="mt-8 rounded-2xl bg-lq-purple-50 border border-lq-purple-100 p-5 text-sm text-lq-ink-700">
                <p><strong>How points work (calm by design):</strong> every finished puzzle awards points. You can play as much or as little as you like — there are no streaks, no penalties for quiet days, no timers during play. Your pace, your pleasure.</p>
            </div>
        </main>
    );
}
